import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn } from 'lucide-react';

/**
 * Lightbox — controlled full-screen image viewer.
 * Pass `src` (string | null) to open/close. Renders through a portal on <body>
 * so it always sits above every section. Closes on backdrop click or Escape.
 */
export const Lightbox = ({ src, alt = '', onClose }) => {
  useEffect(() => {
    if (!src) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [src, onClose]);

  return createPortal(
    <AnimatePresence>
      {src && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-md p-4 lg:p-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          data-testid="lightbox-overlay"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="lightbox-close"
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
          >
            <X size={22} />
          </button>
          <motion.img
            src={src}
            alt={alt}
            className="max-w-full max-h-full object-contain shadow-2xl select-none"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.92 }}
            transition={{ type: 'spring', stiffness: 260, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

/**
 * ZoomableImage — an image that opens itself in the Lightbox when clicked.
 * `className` styles the clickable wrapper, `imgClassName` styles the <img>.
 */
export const ZoomableImage = ({
  src,
  alt = '',
  className = '',
  imgClassName = '',
  style,
  testId,
  hint = true,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Enlarge ${alt}`}
        data-testid={testId}
        className={`group relative block cursor-zoom-in ${className}`}
      >
        <img
          src={src}
          alt={alt}
          className={imgClassName}
          style={style}
          loading="lazy"
          decoding="async"
          draggable={false}
        />
        {hint && (
          <span className="absolute bottom-3 right-3 flex items-center justify-center w-9 h-9 rounded-full bg-black/45 backdrop-blur-sm text-white opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-200 pointer-events-none">
            <ZoomIn size={16} strokeWidth={2} />
          </span>
        )}
      </button>
      <Lightbox src={open ? src : null} alt={alt} onClose={() => setOpen(false)} />
    </>
  );
};
